import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Activity, ChevronDown, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";

function ActivityItem({ item }) {
  return (
    <li className="flex items-start justify-between gap-3 py-2.5" data-testid={`activity-item-${item.id}`}>
      <div className="min-w-0">
        <div className="font-semibold leading-snug">{item.title}</div>
        <div className="mt-0.5 flex flex-wrap gap-x-2 text-xs text-muted-foreground">
          {item.date && <span>{formatDate(item.date)}</span>}
          {item.event?.title && <span>· {item.event.title}</span>}
          {item.details.length > 0 && <span>· {item.details.join(", ")}</span>}
        </div>
      </div>
      {item.url && (
        <a href={item.url} target="_blank" rel="noreferrer" title="Ouvrir dans Notion" className="shrink-0 rounded-full p-1.5 text-stone-400 hover:bg-accent hover:text-primary" data-testid={`activity-notion-link-${item.id}`}>
          <ExternalLink className="h-4 w-4" />
        </a>
      )}
    </li>
  );
}

function ActivityCategory({ personId, category }) {
  const [extra, setExtra] = useState([]);
  const [cursor, setCursor] = useState(category.next_cursor);
  const [loading, setLoading] = useState(false);
  const more = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/persons/${personId}/activity`, { params: { category: category.key, cursor, limit: 50 } });
      const next = data.categories[0];
      setExtra((prev) => [...prev, ...next.items]);
      setCursor(next.next_cursor);
    } finally { setLoading(false); }
  };
  return (
    <div className="py-3" data-testid={`activity-category-${category.key}`}>
      <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-stone-500">
        {category.label}
        <span className="rounded-full bg-accent px-2 py-0.5 text-[11px] text-primary" data-testid={`activity-count-${category.key}`}>{category.count}</span>
      </div>
      <ul className="divide-y divide-border/70">{[...category.items, ...extra].map((i) => <ActivityItem key={i.id} item={i} />)}</ul>
      {cursor && (
        <Button variant="ghost" size="sm" className="mt-1 rounded-full text-primary" onClick={more} disabled={loading} data-testid={`activity-see-all-${category.key}`}>
          <ChevronDown className="h-4 w-4" /> {loading ? "Chargement…" : "Voir tout"}
        </Button>
      )}
    </div>
  );
}

export function PersonActivity({ personId }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["person-activity", personId],
    queryFn: async () => (await api.get(`/persons/${personId}/activity`)).data,
    staleTime: 300_000,
  });
  return (
    <section className="lm-card mt-6 p-6" data-testid="person-activity-section">
      <h2 className="flex items-center gap-2 text-lg font-bold"><Activity className="h-5 w-5 text-primary" /> Activité La Mêlée</h2>
      {isLoading && <p className="mt-3 text-sm text-muted-foreground" data-testid="activity-loading">Chargement de l'activité…</p>}
      {isError && <p className="mt-3 text-sm text-red-600" data-testid="activity-error">Activité momentanément indisponible.</p>}
      {data && !data.categories.length && <p className="mt-3 text-sm text-muted-foreground" data-testid="activity-empty">Aucune activité enregistrée pour le moment.</p>}
      {data && data.categories.length > 0 && (
        <div className="mt-2 grid gap-x-8 md:grid-cols-2">
          {data.categories.map((c) => <ActivityCategory key={c.key} personId={personId} category={c} />)}
        </div>
      )}
    </section>
  );
}
